import type { SceneGroup } from '@lumen5/luminary';

import { getEditorFromSceneGroupId } from 'js/src/data/transcript';
import { getTranscriptPauses } from 'js/src/data/transcript/transcript-utils';
import type { RootState } from 'js/src/libs/RootState';

/**
 * One token of a scene group's transcript. A pause is just another token, sitting between the
 * words it falls between. Words carry their transcript-time span; a pause carries the span of its
 * silence. This is the single shape every agent-facing transcript view and every word-driven
 * placement tool reads, so what the agent sees is exactly what it matches and edits against.
 */
export interface InterleavedWord {
    word: string;
    startTime: number; // milliseconds
    endTime: number; // milliseconds
    isPause?: boolean; // true for a silence/pause span between spoken words
}

/** The internal word text of a pause token. Rendered to the agent as a `<pause seconds="N"/>` tag. */
const PAUSE_WORD = '[pause]';

/**
 * Insert pause markers into the ordered word list at their correct position. A pause sits
 * between the last word that starts before it and the next word. Words are left untouched;
 * only pause entries are added, so the transcript the agent already knew stays stable.
 */
export function interleavePauses(
    words: InterleavedWord[],
    pauses: { startTime: number; endTime: number }[],
): InterleavedWord[] {
    if (!pauses.length) {
        return words;
    }
    const toPauseWord = (pause: { startTime: number; endTime: number }): InterleavedWord => ({
        word: PAUSE_WORD,
        startTime: pause.startTime,
        endTime: pause.endTime,
        isPause: true,
    });

    const result: InterleavedWord[] = [];
    let pauseIndex = 0;
    for (const word of words) {
        while (pauseIndex < pauses.length && pauses[pauseIndex].startTime < word.startTime) {
            result.push(toPauseWord(pauses[pauseIndex]));
            pauseIndex++;
        }
        result.push(word);
    }
    while (pauseIndex < pauses.length) {
        result.push(toPauseWord(pauses[pauseIndex]));
        pauseIndex++;
    }
    return result;
}

/**
 * Pauses live in the Lexical transcript editor (as SilenceNodes), not in Luminary's
 * transcriptData. When Redux state is available we read them from the scene group's editor so the
 * agent can see them; otherwise we degrade to the pause-less word list.
 */
export function getTranscriptPausesForSceneGroup(
    state: RootState | undefined,
    sceneGroup: SceneGroup,
): { startTime: number; endTime: number }[] {
    // The storeless description path passes no state; a scene group with no id cannot key an
    // editor. Otherwise read the mounted editor's pauses — getEditorFromSceneGroupId returns
    // undefined (no crash) when this group's transcript tab isn't mounted, meaning no pauses.
    if (!state || !sceneGroup.id) {
        return [];
    }
    const editor = getEditorFromSceneGroupId(state, sceneGroup.id);
    return editor ? getTranscriptPauses(editor) : [];
}

/**
 * The canonical interleaved (word + pause) transcript for a voice-driven scene group: words from
 * Luminary's transcriptData, pauses from the Lexical editor, merged by time. This is the SINGLE
 * source of truth — the agent's read view (assetSerializer.getTranscriptDescription), the
 * scene-coverage partition, and every word-driven placement tool all derive from this exact list,
 * so the agent sees, matches against, and edits the same tokens everywhere.
 *
 * Returns [] when the group has no transcript (text-on-media, etc.).
 */
export function getInterleavedTranscript(
    state: RootState | undefined,
    sceneGroup: SceneGroup,
): InterleavedWord[] {
    const transcript = sceneGroup.originalTranscript;
    const transcriptWords = transcript?.transcriptData?.words || transcript?.words || [];
    if (transcriptWords.length === 0) {
        return [];
    }
    const words: InterleavedWord[] = transcriptWords.map((w) => ({
        word: w.word,
        startTime: w.startTime,
        endTime: w.endTime,
    }));
    return interleavePauses(words, getTranscriptPausesForSceneGroup(state, sceneGroup));
}

/**
 * Render one interleaved token as the agent reads and types it: a spoken word verbatim, a pause as
 * a `<pause seconds="N"/>` tag whose N is the silence length in seconds (matching the transcript
 * read view). Kept here so the coverage slice, the transcript view, and the words the agent passes
 * back into placement tools all use one notation.
 */
export function interleavedTokenToString(token: InterleavedWord): string {
    if (token.isPause) {
        const seconds = ((token.endTime - token.startTime) / 1000).toFixed(1);
        return `<pause seconds="${seconds}"/>`;
    }
    return token.word;
}
